import { useState, useEffect } from "react";
import { useApp } from "../app/AppContext.jsx";
import { api } from "../lib/api.js";
import { COUNTRIES } from "../i18n/index.js";

/**
 * One form, four shapes: `listing`, `wanted`, and `auction_bid` with or without a lot.
 *
 * A bid on a specific lot needs nothing but contact details. A bid *brief* — "watch the
 * halls for this car" — is the only auction product we can honestly offer while there is no
 * licensed feed, and it needs the car described plus the two things a bid turns on: the
 * lowest inspection grade worth bidding, and the hammer price not to pass.
 *
 * Validation is intentionally light in the browser and authoritative on the server: a
 * buyer who mistypes a phone number should not be blocked from reaching us, but a bot
 * should not reach the desk at all — hence the honeypot rather than a captcha, which would
 * cost real conversions on a page selling €200,000 cars.
 */
export default function EnquiryForm({ kind = "listing", listingId, freightMode, onSuccess }) {
  const { t, country, lang, user } = useApp();
  // Nobody reaches this form without an account any more, so the address is already known
  // and asking for it again is asking a customer to prove something they just proved.
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: "", message: "", website: "",
  });
  useEffect(() => {
    if (user) setForm((f) => ({ ...f, email: f.email || user.email, name: f.name || user.name || "" }));
  }, [user]);
  const [wanted, setWanted] = useState({
    make: "", model: "", yearFrom: "", yearTo: "", budgetEur: "", mileageMaxKm: "", colour: "", notes: "",
    auctionGradeMin: "4", maxHammerJpy: "", steering: "LHD",
  });
  // Either a standing brief the desk works the auction halls against, or a bid on one lot.
  const isBrief = kind === "auction_bid" && !listingId;
  const describesACar = kind === "wanted" || isBrief;
  const [dest, setDest] = useState(country);
  const [state, setState] = useState({ sending: false, result: null, error: null, fields: {} });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setW = (k) => (e) => setWanted((w) => ({ ...w, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setState({ sending: true, result: null, error: null, fields: {} });
    try {
      const res = await api.enquire({
        kind, listingId, country: dest, language: lang, freightMode,
        name: form.name, email: form.email, phone: form.phone || null,
        message: form.message || null,
        website: form.website,                      // honeypot
        wanted: describesACar ? wanted : undefined,
        sourcePage: window.location.pathname + window.location.search,
      });
      setState({ sending: false, result: res, error: null, fields: {} });
      onSuccess?.(res);
    } catch (err) {
      setState({
        sending: false, result: null,
        error: err.body?.error === "validation" ? null : t("enq.error"),
        fields: err.body?.fields || {},
      });
    }
  }

  if (state.result) {
    return (
      <div className="notice">
        <p className="h3" style={{ color: "var(--accent)", marginBottom: 8 }}>{t("enq.success")}</p>
        <p>{t("enq.successBody", { ref: state.result.ref })}</p>
      </div>
    );
  }

  const err = (k) => state.fields[k];

  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }} noValidate>
      {describesACar && (
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 14 }}>
          <div className="field"><label htmlFor="w-make">{t("enq.w.make")}</label><input id="w-make" className="input" value={wanted.make} onChange={setW("make")} placeholder="Porsche" /></div>
          <div className="field"><label htmlFor="w-model">{t("enq.w.model")}</label><input id="w-model" className="input" value={wanted.model} onChange={setW("model")} placeholder="911 GT3" /></div>
          <div className="field"><label htmlFor="w-yf">{t("enq.w.yearFrom")}</label><input id="w-yf" className="input" type="number" inputMode="numeric" value={wanted.yearFrom} onChange={setW("yearFrom")} /></div>
          <div className="field"><label htmlFor="w-yt">{t("enq.w.yearTo")}</label><input id="w-yt" className="input" type="number" inputMode="numeric" value={wanted.yearTo} onChange={setW("yearTo")} /></div>
          <div className="field"><label htmlFor="w-b">{t("enq.w.budget")}</label><input id="w-b" className="input" type="number" inputMode="numeric" value={wanted.budgetEur} onChange={setW("budgetEur")} /></div>
          <div className="field"><label htmlFor="w-km">{t("enq.w.mileage")}</label><input id="w-km" className="input" type="number" inputMode="numeric" value={wanted.mileageMaxKm} onChange={setW("mileageMaxKm")} /></div>
          {isBrief && (
            <>
              <div className="field">
                <label htmlFor="w-g">{t("enq.w.gradeMin")}</label>
                <select id="w-g" className="select" value={wanted.auctionGradeMin} onChange={setW("auctionGradeMin")}>
                  {["5", "4.5", "4", "3.5", "R"].map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
                <span className="tiny muted">{t("enq.w.gradeMinHelp")}</span>
              </div>
              <div className="field">
                <label htmlFor="w-h">{t("enq.w.maxHammer")}</label>
                <input id="w-h" className="input" type="number" inputMode="numeric" value={wanted.maxHammerJpy} onChange={setW("maxHammerJpy")} placeholder="8000000" />
                <span className="tiny muted">{t("enq.w.maxHammerHelp")}</span>
              </div>
              <div className="field">
                <label htmlFor="w-s">{t("car.spec.steering")}</label>
                <select id="w-s" className="select" value={wanted.steering} onChange={setW("steering")}>
                  <option value="LHD">{t("stock.steering.LHD")}</option>
                  <option value="RHD">{t("stock.steering.RHD")}</option>
                </select>
              </div>
            </>
          )}
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label htmlFor="w-n">{t("enq.w.notes")}</label>
            <textarea id="w-n" className="textarea" value={wanted.notes} onChange={setW("notes")} />
          </div>
        </div>
      )}

      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 14 }}>
        <div className={`field${err("name") ? " field--error" : ""}`}>
          <label htmlFor="e-name">{t("enq.name")}</label>
          <input id="e-name" aria-invalid={!!err("name")} aria-describedby={err("name") ? "e-name-error" : undefined} className="input" value={form.name} onChange={set("name")} required autoComplete="name" />
          {err("name") && <span id="e-name-error" role="alert" className="field__error">{t("common.required")}</span>}
        </div>

        <div className={`field${err("email") ? " field--error" : ""}`}>
          <label htmlFor="e-email">{t("enq.email")}</label>
          <input id="e-email" aria-invalid={!!err("email")} aria-describedby={err("email") ? "e-email-error" : undefined} className="input" type="email" value={form.email} onChange={set("email")} required autoComplete="email" />
          {err("email") && <span id="e-email-error" role="alert" className="field__error">{t("common.required")}</span>}
        </div>

        <div className="field">
          <label htmlFor="e-phone">{t("enq.phone")} <span className="muted">({t("common.optional")})</span></label>
          <input id="e-phone" className="input" type="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" />
        </div>

        <div className="field">
          <label htmlFor="e-country">{t("enq.country")}</label>
          <select id="e-country" className="select" value={dest} onChange={(e) => setDest(e.target.value)}>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.name[lang] || c.name.en}</option>)}
          </select>
        </div>
      </div>

      <div className="field">
        <label htmlFor="e-msg">{t("enq.message")}</label>
        <textarea id="e-msg" className="textarea" value={form.message} onChange={set("message")} placeholder={t("enq.messagePlaceholder")} />
      </div>

      {/* Honeypot — invisible to people, irresistible to bots. */}
      <input className="hp" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={set("website")} name="website" />

      {state.error && <div role="alert" className="notice notice--danger">{state.error}</div>}

      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <button className="btn btn--gold" type="submit" disabled={state.sending}>
          {state.sending ? t("enq.sending") : t("enq.submit")}
        </button>
        <span className="tiny muted">{t("enq.consent")}</span>
      </div>
    </form>
  );
}
